// CSV import with row/column validation errors, plus full data export.
import React, { useState } from 'react';
import { api, download, ApiError } from '../api';
import { ErrorBox } from '../ui';

const IMPORTABLE = [
  { key: 'people', label: 'Personnel' },
  { key: 'attendance', label: 'Attendance periods' },
  { key: 'leave', label: 'Leave' },
  { key: 'providers', label: 'Accommodation providers' },
  { key: 'bookings', label: 'Accommodation bookings' },
  { key: 'companies', label: 'Companies' },
  { key: 'properties', label: 'Properties' },
  { key: 'rooms', label: 'Rooms' }
];

export default function ImportExport() {
  const [entity, setEntity] = useState('people');
  const [csv, setCsv] = useState('');
  const [errors, setErrors] = useState<{ row: number; column: string; message: string }[]>([]);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const runImport = async (dryRun: boolean) => {
    setBusy(true);
    setErrors([]);
    setResult(null);
    try {
      const r = await api(`/import/${entity}`, { method: 'POST', body: { csv, dry_run: dryRun } });
      setResult(dryRun ? `Validation passed — ${r.rows} row(s) ready to import.` : `Imported ${r.inserted} row(s).`);
      if (!dryRun) setCsv('');
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 400) {
        // server returns structured row errors
        const body = e.errors;
        setErrors(body.map((msg: string) => {
          const m = msg.match(/^Row (\d+) ?(\S*): (.*)$/);
          return m ? { row: Number(m[1]), column: m[2], message: m[3] } : { row: 0, column: '', message: msg };
        }));
      } else setErrors([{ row: 0, column: '', message: e.message }]);
    } finally {
      setBusy(false);
    }
  };

  const onFile = (f: File | null) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsv(String(reader.result || ''));
    reader.readAsText(f);
  };

  return (
    <div className="page">
      <div className="page-head"><h2>Import &amp; export</h2></div>

      <div className="panel">
        <h3>Import from CSV</h3>
        <div className="btn-row" style={{ marginBottom: 10 }}>
          <select className="input" value={entity} onChange={e => setEntity(e.target.value)}>
            {IMPORTABLE.map(i => <option key={i.key} value={i.key}>{i.label}</option>)}
          </select>
          <button className="btn" onClick={() => download(`/import/template/${entity}`)}>Download template</button>
          <label className="btn">
            Choose CSV file…
            <input type="file" accept=".csv,text/csv" style={{ display: 'none' }}
              onChange={e => onFile(e.target.files?.[0] || null)} />
          </label>
        </div>
        <textarea className="input" rows={8} placeholder="…or paste CSV content here (first row must be the column headers; dates as DD/MM/YYYY)"
          value={csv} onChange={e => setCsv(e.target.value)} style={{ fontFamily: 'Consolas, monospace' }} />
        <div className="btn-row" style={{ marginTop: 10 }}>
          <button className="btn" disabled={busy || !csv.trim()} onClick={() => runImport(true)}>Validate only</button>
          <button className="btn btn-primary" disabled={busy || !csv.trim()} onClick={() => runImport(false)}>Validate &amp; import</button>
        </div>
        {result && <p style={{ color: 'var(--olive-dark)', fontWeight: 600 }}>{result}</p>}
        {errors.length > 0 && (
          <div className="error-box" style={{ marginTop: 10 }}>
            <strong>Import blocked — fix these first (nothing was saved):</strong>
            <table style={{ marginTop: 6 }}>
              <thead><tr><th>Row</th><th>Column</th><th>Problem</th></tr></thead>
              <tbody>
                {errors.map((e, i) => <tr key={i}><td>{e.row || ''}</td><td>{e.column}</td><td>{e.message}</td></tr>)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel">
        <h3>Export</h3>
        <p className="muted">Everything can be exported at any time. CSV files open directly in Excel.</p>
        <div className="btn-row" style={{ flexWrap: 'wrap' }}>
          {IMPORTABLE.map(i => (
            <button key={i.key} className="btn" onClick={() => download(`/export/csv/${i.key}`)}>{i.label} CSV</button>
          ))}
          <button className="btn" onClick={() => download('/export/csv/allocations')}>Room allocations CSV</button>
          <button className="btn" onClick={() => download('/export/csv/travel')}>Travel CSV</button>
        </div>
        <div className="btn-row" style={{ marginTop: 10 }}>
          <button className="btn btn-primary" onClick={() => download('/export/json')}>Full JSON backup</button>
          <button className="btn btn-primary" onClick={() => download('/export/db')}>Download SQLite database copy</button>
        </div>
      </div>
    </div>
  );
}
